/**
 * AuthContext — thin compatibility layer over the Zustand auth store.
 *
 * - `AuthProvider` triggers `checkAuth()` on mount so the cookie-based
 *   session is validated once per page load.
 * - `useAuth()` returns the same shape callers have always expected,
 *   but reads from the Zustand store under the hood.
 *
 * New code should import `useAuthStore` directly from `stores/useAuthStore`.
 */

import { useEffect, type ReactNode } from "react";
import { useAuthStore, type AuthStore } from "../stores/useAuthStore";

export type { AuthUser } from "../stores/useAuthStore";

/**
 * Initializes auth on mount by calling `checkAuth`.
 * Keeps the same provider position in the tree so FeatureFlagProvider,
 * PermissionProvider, etc. continue to work unchanged.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const checkAuth = useAuthStore((s) => s.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return <>{children}</>;
}

/**
 * Drop-in replacement hook — existing consumers keep importing this.
 * Returns the full store slice so destructuring still works:
 *   const { isAuthenticated, user, login, logout } = useAuth();
 */
export function useAuth(): AuthStore {
  return useAuthStore();
}
